import { bool, func, object, shape, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import './SLUAgreementContainer.scss';
import TextInputMP from 'ps-components/TextInputMP/TextInputMP';
import * as sluActions from './SLUAgreementActions';
import { withRouter } from 'react-router';
import { SUBMIT_STATUS_FAILURE, SUBMIT_STATUS_IN_PROGRESS, SUBMIT_STATUS_SUCCESS } from './SLUAgreementReducer';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import SlideoutError from 'ps-components/Slideout/Error/SlideoutError';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { SLIDEOUTS } from '../../Slideout/Slideouts';
import { CONTACT_SUPPORT_MESSAGE } from '../../../lib/constants';

const propTypes = {
  history: object.isRequired,
  submitStatus: string.isRequired,
  userSignature: string,
  userAcceptance: bool.isRequired,
  updateUserSignature: func.isRequired,
  updateUserAcceptance: func.isRequired,
  submitAgreement: func.isRequired,
  closeError: func.isRequired,
  hideSlideout: func.isRequired,
  slideout: shape({
    slideoutProps: shape({
      classContainerName: string
    }).isRequired
  }).isRequired,
  showSlideout: func.isRequired
};

const renderStatusModifier = props => {
  switch (props.submitStatus) {
    case SUBMIT_STATUS_IN_PROGRESS:
      return <ProgressStatusOverlay primaryText="Submitting Updates..." secondaryText="This might take a moment" />;
    case SUBMIT_STATUS_FAILURE:
      return (
        <SlideoutError className="slu-agreement__error" doClose={props.closeError}>
          <h4>Failed to submit the updates. Please try again.</h4>
          <div className="slu-agreement-error__instructions">
            {CONTACT_SUPPORT_MESSAGE}
          </div>
        </SlideoutError>
      );
    default:
      return null;
  }
};

const submitAgreement = props => {
  if (props.slideout?.slideoutProps?.submitAgreement) {
    props.slideout?.slideoutProps?.submitAgreement(props.emailEnabled, props.userSignature);
  } else {
    props.submitAgreement();
  }
};

const renderAgreementPage = props => {
  const { userAcceptance, userSignature } = props;
  const saveEnabled = userAcceptance && userSignature;

  return (
    <React.Fragment>
      <h3 className="slu-agreement__header">Submit Survey Library Updates</h3>
      <p className="slu-agreement__legal">
        By submitting this form on behalf of the organization you represent (“<b>Customer</b>”), you (i) confirm that
        Customer is instructing Payscale, Inc. (“<b>Payscale</b>”) to load the third-party surveys selected into
        Customer’s account in accordance with the relevant agreement between Customer and Payscale (“<b>Agreement</b>”)
        and (ii) represent that you have the right and authority to act on Customer’s behalf.
      </p>
      <div className="slu-agreement__footer">
        <div className="slu-agreement-footer__top-container">
          <CheckboxInput checked={props.userAcceptance} onClick={props.updateUserAcceptance} />
          <label className="slu-agreement-footer__checkbox-label">
            I have read and agree to above Terms and Conditions
          </label>
        </div>
        <div className="slu-agreement-footer__bottom-container">
          <label className="slu-agreement-footer__name-label">My Name</label>
          <TextInputMP
            className="slu-agreement-footer__name-input"
            value={props.userSignature}
            onChange={props.updateUserSignature}
          />
          <a className="slu-agreement-footer__cancel" onClick={props.hideSlideout}>
            Cancel
          </a>
          <button
            className={`pxl-btn pxl-btn-primary slu-agreement-footer__save${saveEnabled ? '' : ' disabled'}`}
            onClick={saveEnabled ? () => submitAgreement(props) : null}
          >
            Sign & Submit
          </button>
        </div>
      </div>
    </React.Fragment>
  );
};

const renderSuccessPage = props => {
  return (
    <SlideoutSubmitSuccess
      className="slu-agreement__success"
      submitSuccessTitle="Updates Submitted!"
      submitSuccessDetails={
        'An email confirmation with a signed PDF attached will be sent out to you and your ' +
        "Client Success Manager. If you don't receive it, please contact support."
      }
      classNameDetails="slu-agreement-success__details"
      actionText="Done"
      classNameAction="slu-agreement-success__action"
      actionFunction={props.hideSlideout}
    />
  );
};

const SLUAgreementContainer = props => {
  const { classContainerName } = props.slideout.slideoutProps;
  const { submitStatus } = props;

  const renderContent = submitStatus === SUBMIT_STATUS_SUCCESS ? renderSuccessPage : renderAgreementPage;
  return (
    <div className={`slu-agreement ${classContainerName}`}>
      <div className="slu-agreement__close icon-cancel" onClick={props.hideSlideout} />
      {renderStatusModifier(props)}
      {renderContent(props)}
    </div>
  );
};

SLUAgreementContainer.propTypes = propTypes;

const mapStateToProps = ({ surveyLibraryUpdateAgreement }) => {
  return {
    ...surveyLibraryUpdateAgreement
  };
};

const actions = {
  ...sluActions,
  showSlideout
};

export default connect(mapStateToProps, actions)(withRouter(SLUAgreementContainer));
